import { type QueryClient } from '@tanstack/react-query';

import {
  getGetAllClassroomsQueryKey,
  getGetAssignmentStatsQueryKey,
  getGetClassGroupDetailQueryKey as getGetClassroomDetailQueryKey,
  getGetClassLinksQueryKey,
  getGetClassMembersQueryKey,
  getGetJoinedClassroomsQueryKey,
  getGetMyClassGroupsQueryKey,
  getGetOwnedClassroomsQueryKey,
} from '@/api/gen/classroom/classroom.gen';
import { getGetMyClassroomsQueryKey as getGetMyClassroomsStudentQueryKey } from '@/api/gen/classroom-student/classroom-student.gen';

export const invalidateClassroomListQueries = (qc: QueryClient) =>
  Promise.all([
    qc.invalidateQueries({ queryKey: getGetMyClassGroupsQueryKey() }),
    qc.invalidateQueries({ queryKey: getGetMyClassroomsStudentQueryKey() }),
    qc.invalidateQueries({ queryKey: getGetOwnedClassroomsQueryKey() }),
    qc.invalidateQueries({ queryKey: getGetJoinedClassroomsQueryKey() }),
    qc.invalidateQueries({ queryKey: getGetAllClassroomsQueryKey() }),
  ]);

export const invalidateClassroomDetailQueries = (
  qc: QueryClient,
  classroomId: string,
) =>
  Promise.all([
    qc.invalidateQueries({
      queryKey: getGetClassroomDetailQueryKey(classroomId),
    }),
    qc.invalidateQueries({
      queryKey: getGetClassLinksQueryKey(classroomId),
    }),
    qc.invalidateQueries({
      queryKey: getGetClassMembersQueryKey(classroomId),
    }),
    // 과제 카드의 "참여 N / 전체 M" 분모는 명단 인원수라, 학생 추가·삭제·변경 뒤 같이 다시 받는다.
    qc.invalidateQueries({
      queryKey: getGetAssignmentStatsQueryKey(classroomId),
    }),
    // 학생 상세/통계 페이지(`/classrooms/{id}/members/{userId}/stats`)는 별도
    // query key 로 동작하므로 prefix predicate 로 같이 무효화한다. 이름 수정 등의
    // 회원 정보 변경이 상세 페이지에 즉시 반영되도록 한다.
    qc.invalidateQueries({
      predicate: query => {
        const key = query.queryKey[0];
        return (
          typeof key === 'string' &&
          key.startsWith(`/classrooms/${classroomId}/members/`)
        );
      },
    }),
  ]);

// link/unlink 변경 시 갱신이 필요한 쿼리만 좁혀서 무효화.
// classroom 리스트(owned/joined/all) 응답은 카드 표시에 link 상태/카운트를 쓰지 않으므로 제외.
// myClassrooms 는 ShareModal 의 batch `linkedContentId` 응답이 달라지므로 포함.
export const invalidateClassroomMutationQueries = (
  qc: QueryClient,
  classroomId: string,
) =>
  Promise.all([
    qc.invalidateQueries({
      queryKey: getGetClassroomDetailQueryKey(classroomId),
    }),
    qc.invalidateQueries({
      queryKey: getGetClassLinksQueryKey(classroomId),
    }),
    qc.invalidateQueries({ queryKey: getGetMyClassGroupsQueryKey() }),
    // 연결·해제한 과제의 참여·정답률 집계도 같이 갱신한다.
    qc.invalidateQueries({
      queryKey: getGetAssignmentStatsQueryKey(classroomId),
    }),
  ]);
